/**
 * @fileoverview Fires a rich custom toast when new orders are detected.
 * Shares the same TanStack Query cache key as useTodaysOrders so no extra
 * network requests are made when both hooks are mounted.
 * @module features/daily-summary/hooks/useOrderNotifications
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { salesService } from '@/features/orders/api/salesService'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { groupSalesByOrder } from './useTodaysOrders'
import type { OrderGroup } from './useTodaysOrders'
import { highlightNewOrders } from './newOrderHighlights'
import { OrderNotificationToast } from '../components/OrderNotificationToast'

// Dev helper: run `__triggerOrderToast(3)` in Chrome console to preview toast
if (import.meta.env.DEV) {
  ;(window as unknown as Record<string, unknown>).__triggerOrderToast = (
    count = 1,
  ) => {
    const mockOrders: OrderGroup[] = Array.from({ length: count }, (_, i) => ({
      orderId: `mock-${Date.now()}-${i}`,
      displayOrderId: `${7842 + i}`,
      country: ['PL', 'DE', 'FR', 'GB'][i % 4] ?? 'PL',
      orderSource: ['Allegro', 'Holivica Poland', 'Personal'][i % 3] ?? null,
      total: 1249 + i * 500,
      lines: [
        {
          sku: '1',
          skuKey: null,
          name: 'Premium Wireless Headphones',
          imageUrl: null,
          quantity: 2,
          amount: 1249 + i * 500,
        },
      ],
      maxSaleId: Date.now() + i,
    }))

    // Also highlight mock orders in OrderFeed (if visible)
    highlightNewOrders(mockOrders.map((o) => o.orderId))

    toast.custom(
      (id) => (
        <OrderNotificationToast
          toastId={id}
          newOrders={mockOrders}
          currency="PLN"
        />
      ),
      { duration: 8_000 },
    )
  }
}

/** Routes where toast notifications are shown. */
const NOTIFICATION_ROUTES = new Set(['/dashboard', '/dashboard/performance'])

/**
 * Polls today's order count and shows a custom toast when new orders arrive.
 *
 * - First fetch establishes the baseline (no toast).
 * - Subsequent fetches fire a toast only for genuinely new orderIds.
 * - Toasts are suppressed on routes outside {@link NOTIFICATION_ROUTES}.
 */
export function useOrderNotifications(): void {
  const location = useLocation()
  const todayStr = toDateOnlyStringLocal(new Date())
  const { currency } = useReportingCurrency()
  const cur = currency ?? 'PLN'

  const onDashboard = location.pathname.startsWith('/dashboard')
  const prevOrderIdsRef = useRef<Set<string> | null>(null)

  const { data } = useQuery({
    queryKey: ['sales', 'daily-summary', todayStr],
    queryFn: () =>
      salesService.getSales({
        startDate: todayStr,
        endDate: todayStr,
        sort: 'date',
        direction: 'desc',
        size: 200,
        page: 0,
      }),
    enabled: onDashboard,
    staleTime: 60_000,
    refetchInterval: onDashboard ? 60_000 : false,
  })

  useEffect(() => {
    if (!data) return

    const orders = groupSalesByOrder(data.content)
    const currentIds = new Set(orders.map((o) => o.orderId))
    const prev = prevOrderIdsRef.current

    // First fetch: set baseline, no toast
    if (prev === null) {
      prevOrderIdsRef.current = currentIds
      return
    }

    // Identify genuinely new orders
    const newOrders = orders.filter((o) => !prev.has(o.orderId))
    prevOrderIdsRef.current = currentIds

    if (newOrders.length === 0) return
    if (!NOTIFICATION_ROUTES.has(location.pathname)) return

    // Mark new orders for badge highlights in OrderGroupCard
    highlightNewOrders(newOrders.map((o) => o.orderId))

    toast.custom(
      (id) => (
        <OrderNotificationToast
          toastId={id}
          newOrders={newOrders}
          currency={cur}
        />
      ),
      { duration: 8_000 },
    )
  }, [data, location.pathname, cur])
}
